import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  Alert,
  Paper,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Notification, NotificationType, DeliveryType, TargetAudience, Student, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';

interface NotificationsManagementProps {
  permissions: CrudPermissions;
  onViewRecipients?: (notificationId: string) => void;
}

export function NotificationsManagement({ permissions, onViewRecipients }: NotificationsManagementProps) {
  const { notifications, setNotifications, students } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    notificationType: 'both' as NotificationType,
    deliveryType: 'manual' as DeliveryType,
    targetAudience: [] as TargetAudience[],
    specificStudentIds: [] as string[],
    scheduledAt: '',
  });

  // Tour-driven slow scroll (step 19). Booth narrative shows the
  // template list scrolling top-to-bottom on its own — no cursor, no
  // spotlight, just a calm pan over the page so the audience can see
  // the breadth of templates available. Cleanup: scrolls back to the
  // top when the step changes (detected by any other trigger after
  // ours fired).
  useEffect(() => {
    let ownerStepId: string | null = null;
    let rafId = 0;
    let cancelled = false;
    const cancel = () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
    const animateScroll = (toY: number, durationMs: number) => {
      cancel();
      cancelled = false;
      const startY = window.scrollY;
      const baseDelta = toY - startY;
      if (Math.abs(baseDelta) < 8) return;
      const start = performance.now();
      const step = (now: number) => {
        if (cancelled) return;
        const t = Math.min(1, (now - start) / durationMs);
        // Linear easing — even pace from top to bottom. The previous
        // cubic ease-in-out spent the first half barely moving and
        // the second half accelerating, which read as "scrolls a
        // little, then jumps to the bottom."
        // Recompute the target each frame so any lazy-loaded content
        // that grows the page during the scroll doesn't clip us short
        // of the actual bottom.
        const liveMax = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight,
        );
        const target = Math.min(toY, liveMax);
        const liveDelta = target - startY;
        window.scrollTo({ top: startY + liveDelta * t, behavior: 'auto' });
        if (t < 1) rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
    };
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const stepId: string | undefined = detail?.stepId;
      if (detail?.trigger === 'notifications-slow-scroll') {
        ownerStepId = stepId ?? null;
        // Wait a beat so the page is mounted/measured, then animate
        // to the bottom over the bulk of the step's autoplay window.
        window.setTimeout(() => {
          if (cancelled) return;
          const max = Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight,
          );
          animateScroll(max, 9000);
        }, 600);
      } else if (ownerStepId !== null && stepId !== ownerStepId) {
        ownerStepId = null;
        cancel();
        animateScroll(0, 600);
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => {
      window.removeEventListener('cc-tour-trigger', onTrigger);
      cancel();
    };
  }, []);

  const handleOpenDialog = (notification?: Notification) => {
    if (notification) {
      setEditingNotification(notification);
      setFormData({
        title: notification.title,
        content: notification.content,
        notificationType: notification.notificationType,
        deliveryType: notification.deliveryType,
        targetAudience: notification.targetAudience,
        specificStudentIds: notification.specificStudentIds || [],
        scheduledAt: notification.scheduledAt ? new Date(notification.scheduledAt).toISOString().slice(0, 16) : '',
      });
    } else {
      setEditingNotification(null);
      setFormData({
        title: '',
        content: '',
        notificationType: 'both',
        deliveryType: 'manual',
        targetAudience: [],
        specificStudentIds: [],
        scheduledAt: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNotification(null);
  };

  const handleSubmit = () => {
    if (editingNotification) {
      setNotifications(notifications.map(n =>
        n.id === editingNotification.id
          ? {
              ...n,
              ...formData,
              scheduledAt: formData.scheduledAt ? new Date(formData.scheduledAt) : undefined,
              updatedAt: new Date(),
            }
          : n
      ));
    } else {
      const newNotification: Notification = {
        id: crypto.randomUUID(),
        ...formData,
        scheduledAt: formData.scheduledAt ? new Date(formData.scheduledAt) : undefined,
        status: 'draft',
        createdBy: 'admin-id',
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setNotifications([...notifications, newNotification]);
    }
    handleCloseDialog();
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this notification?')) {
      setNotifications(notifications.filter(n => n.id !== id));
    }
  };

  const handleSendNow = (notification: Notification) => {
    if (confirm('Are you sure you want to send this notification now?')) {
      setNotifications(notifications.map(n =>
        n.id === notification.id
          ? { ...n, status: 'sent', sentAt: new Date() }
          : n
      ));
    }
  };

  const handleTargetAudienceChange = (audience: TargetAudience) => {
    setFormData(prev => {
      const newAudience = prev.targetAudience.includes(audience)
        ? prev.targetAudience.filter(a => a !== audience)
        : [...prev.targetAudience, audience];

      return {
        ...prev,
        targetAudience: newAudience,
        specificStudentIds: audience === 'students' && newAudience.includes('students')
          ? []
          : prev.specificStudentIds,
      };
    });
  };

  const handleSpecificStudentsChange = (students: Student[]) => {
    const studentIds = students.map(s => s.id);
    setFormData(prev => ({
      ...prev,
      specificStudentIds: studentIds,
      targetAudience: studentIds.length > 0
        ? prev.targetAudience.filter(a => a !== 'students')
        : prev.targetAudience,
    }));
  };

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const matchesSearch =
        notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        notification.content.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [notifications, searchQuery]);

  const getNotificationTypeConfig = (type: NotificationType) => {
    const config = {
      system: { icon: <NotificationsIcon fontSize="small" />, label: 'System', color: 'info' as const },
      email: { icon: <EmailIcon fontSize="small" />, label: 'Email', color: 'secondary' as const },
      both: { icon: <NotificationsIcon fontSize="small" />, label: 'System & Email', color: 'primary' as const },
    };
    return config[type];
  };

  const getStatusColor = (status: string) => {
    const colorMap = {
      draft: 'default' as const,
      scheduled: 'warning' as const,
      sent: 'success' as const,
    };
    return colorMap[status as keyof typeof colorMap];
  };

  return (
    <Box data-tour="notifications-templates" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Notifications Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and send notifications to users
          </Typography>
        </Box>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Create Notification
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search notifications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(auto-fill, minmax(320px, 1fr))',
          },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {filteredNotifications.map((notification) => {
          const typeConfig = getNotificationTypeConfig(notification.notificationType);
          return (
            <Box
              key={notification.id}
              sx={{
                display: 'flex',
                minWidth: 0,
              }}
            >
              <Card
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ flexGrow: 1, minWidth: 0, maxWidth: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                      }}
                    >
                      {notification.title}
                    </Typography>
                    <Chip
                      label={notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}
                      color={getStatusColor(notification.status)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {notification.content}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip
                      icon={typeConfig.icon}
                      label={typeConfig.label}
                      color={typeConfig.color}
                      size="small"
                    />
                    <Chip
                      icon={notification.deliveryType === 'automated' ? <ScheduleIcon fontSize="small" /> : undefined}
                      label={notification.deliveryType.charAt(0).toUpperCase() + notification.deliveryType.slice(1)}
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  {notification.scheduledAt && notification.status === 'scheduled' && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        border: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ScheduleIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Scheduled For:
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {new Date(notification.scheduledAt).toLocaleString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                      Target Audience:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {notification.targetAudience.map(audience => (
                        <Chip
                          key={audience}
                          label={audience === 'students' ? 'All Students' : audience === 'parents' ? 'All Parents' : 'All Community Members'}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {notification.specificStudentIds && notification.specificStudentIds.length > 0 && (
                        <Chip
                          label={`${notification.specificStudentIds.length} Specific Student${notification.specificStudentIds.length > 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Box>
                    {notification.specificStudentIds && notification.specificStudentIds.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {notification.specificStudentIds.slice(0, 3).map(studentId => {
                          const student = students.find(s => s.id === studentId);
                          return student ? (
                            <Chip
                              key={studentId}
                              label={`${student.firstName} ${student.lastName}`}
                              size="small"
                              color="primary"
                            />
                          ) : null;
                        })}
                        {notification.specificStudentIds.length > 3 && (
                          <Chip
                            label={`+${notification.specificStudentIds.length - 3} more`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        )}
                      </Box>
                    )}
                  </Box>

                  {notification.sentAt && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" color="success.main" fontWeight={500}>
                        Sent:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(notification.sentAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {notification.status === 'sent' && onViewRecipients && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => onViewRecipients(notification.id)}
                        sx={{
                          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                          color: 'white',
                          textTransform: 'none',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                          },
                        }}
                      >
                        View Recipients
                      </Button>
                    )}
                    {notification.status === 'draft' && (
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        startIcon={<SendIcon />}
                        onClick={() => handleSendNow(notification)}
                        sx={{ textTransform: 'none' }}
                      >
                        Send Now
                      </Button>
                    )}
                    {permissions.update && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(notification)}
                        sx={{ textTransform: 'none' }}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                  {permissions.delete && (
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDelete(notification.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Box>
          );
        })}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNotification ? 'Edit Notification' : 'Create Notification'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />

            <TextField
              label="Content"
              fullWidth
              required
              multiline
              rows={6}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              helperText="Enter the notification message content"
            />

            <FormControl fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                value={formData.notificationType}
                label="Notification Type"
                onChange={(e) => setFormData({ ...formData, notificationType: e.target.value as NotificationType })}
              >
                <MenuItem value="system">System Only</MenuItem>
                <MenuItem value="email">Email Only</MenuItem>
                <MenuItem value="both">System & Email</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Delivery Type</InputLabel>
              <Select
                value={formData.deliveryType}
                label="Delivery Type"
                onChange={(e) => setFormData({ ...formData, deliveryType: e.target.value as DeliveryType })}
              >
                <MenuItem value="manual">Send Manually</MenuItem>
                <MenuItem value="automated">Schedule for Later</MenuItem>
              </Select>
            </FormControl>

            {formData.deliveryType === 'automated' && (
              <TextField
                label="Schedule Date & Time"
                type="datetime-local"
                fullWidth
                required
                value={formData.scheduledAt}
                onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            )}

            <FormControl component="fieldset">
              <FormLabel component="legend">Target Audience *</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.targetAudience.includes('students')}
                      onChange={() => handleTargetAudienceChange('students')}
                      disabled={formData.specificStudentIds.length > 0}
                    />
                  }
                  label="All Students"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.targetAudience.includes('parents')}
                      onChange={() => handleTargetAudienceChange('parents')}
                    />
                  }
                  label="All Parents"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.targetAudience.includes('community')}
                      onChange={() => handleTargetAudienceChange('community')}
                    />
                  }
                  label="All Community Members"
                />
              </FormGroup>
            </FormControl>

            <Autocomplete
              multiple
              options={students}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.studentIdNumber})`}
              value={students.filter(s => formData.specificStudentIds.includes(s.id))}
              onChange={(_e, newValue) => handleSpecificStudentsChange(newValue)}
              disabled={formData.targetAudience.includes('students')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Specific Students"
                  placeholder="Select individual students..."
                  helperText={formData.targetAudience.includes('students') ? 'Disabled when "All Students" is selected' : 'Select specific students to notify'}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.studentIdNumber} - Grade {option.grade}
                    </Typography>
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`${option.firstName} ${option.lastName}`}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />

            {formData.targetAudience.length === 0 && formData.specificStudentIds.length === 0 && (
              <Alert severity="warning">
                Please select at least one target audience or specific students
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.title || !formData.content || (formData.targetAudience.length === 0 && formData.specificStudentIds.length === 0)}
          >
            {editingNotification ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
